import React, { useState, useEffect } from 'react';
import { useNetwork } from '@/providers/NetworkProvider';
import { formatToken, formatHashPower } from '@/utils/format';
import { ethers } from 'ethers';
import '@/styles/hacker-aesthetic.css';
import '@/styles/analytics.css';

interface AnalyticsData {
  totalNodes: number;
  activeNodes: number;
  totalHashPower: number;
  totalRewardsClaimed: string;
  playerRank: number;
}

interface AnalyticsPanelProps {
  onClose?: () => void;
}

export const AnalyticsPanel: React.FC<AnalyticsPanelProps> = ({ onClose }) => {
  const { contracts, isConnected, address } = useNetwork();
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isConnected && contracts.gameLogic && address) {
      loadAnalytics();
    } else {
      setLoading(false);
    }
  }, [isConnected, contracts, address]);

  useEffect(() => {
    if (!isConnected || !contracts.gameLogic || !address) return;
    const interval = setInterval(() => {
      void loadAnalytics();
    }, 30000);
    return () => clearInterval(interval);
  }, [isConnected, contracts, address]);

  const loadAnalytics = async () => {
    if (!address || !contracts?.nodeNFT) return;

    try {
      setLoading(true);

      const nodeIds = await contracts.nodeNFT.getNodesByOwner(address);
      let activeNodes = 0;
      let totalHashPower = 0;
      let totalRewardsClaimedWei = 0n;

      for (const nodeId of nodeIds) {
        const stats = await contracts.nodeNFT.getNodeStats(nodeId);
        if (stats.isActive) activeNodes += 1;
        totalHashPower += Number(stats.hashPower);
        totalRewardsClaimedWei += BigInt(stats.totalEarned.toString());
      }

      let playerRank = 0;
      try {
        if (contracts.leaderboard) {
          const rank = await contracts.leaderboard.getPlayerRank(address);
          playerRank = Number(rank);
        }
      } catch {
        playerRank = 0;
      }

      setAnalytics({
        totalNodes: nodeIds.length,
        activeNodes,
        totalHashPower,
        totalRewardsClaimed: ethers.formatEther(totalRewardsClaimedWei),
        playerRank,
      });
    } catch (error) {
      console.error('Failed to load analytics:', error);
      setAnalytics(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="an-panel">
        <div className="an-header">
          <div className="an-title">ANALYTICS</div>
          {onClose && (
            <button type="button" className="close-button" onClick={onClose} aria-label="Close">
              ×
            </button>
          )}
        </div>
        <div className="an-skeleton">
          <div className="loading-skeleton an-sk-card" />
          <div className="loading-skeleton an-sk-card" />
          <div className="loading-skeleton an-sk-card" />
          <div className="loading-skeleton an-sk-card" />
          <div className="loading-skeleton an-sk-bar" />
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="an-panel">
        <div className="an-header">
          <div className="an-title">ANALYTICS</div>
          {onClose && (
            <button type="button" className="close-button" onClick={onClose} aria-label="Close">
              ×
            </button>
          )}
        </div>
        <div className="an-empty">
          <span>NO ANALYTICS DATA AVAILABLE</span>
        </div>
      </div>
    );
  }

  const utilization = analytics.totalNodes > 0
    ? Math.round((analytics.activeNodes / analytics.totalNodes) * 100)
    : 0;

  return (
    <div className="an-panel">
      <div className="an-header">
        <div className="an-title">ANALYTICS</div>
        <span className="an-rank-chip">RANK #{analytics.playerRank}</span>
        {onClose && (
          <button type="button" className="close-button" onClick={onClose} aria-label="Close">
            ×
          </button>
        )}
      </div>

      <div className="an-body">
        <div className="an-section-label">OVERVIEW</div>
        <div className="an-kpi-grid">
          <div className="an-kpi-card">
            <span className="an-kpi-value">{analytics.totalNodes}</span>
            <span className="an-kpi-label">TOTAL NODES</span>
          </div>
          <div className="an-kpi-card">
            <span className="an-kpi-value">{analytics.activeNodes}</span>
            <span className="an-kpi-label">ACTIVE NODES</span>
          </div>
          <div className="an-kpi-card">
            <span className="an-kpi-value">{formatHashPower(analytics.totalHashPower)}</span>
            <span className="an-kpi-label">TOTAL HASH</span>
          </div>
          <div className="an-kpi-card">
            <span className="an-kpi-value">{formatToken(analytics.totalRewardsClaimed)}</span>
            <span className="an-kpi-label">REWARDS CLAIMED</span>
          </div>
        </div>

        <div className="an-section-label">FLEET UTILIZATION</div>
        <div className="an-bar-card">
          <div className="an-bar-row">
            <span className="an-bar-caption">
              {analytics.activeNodes} / {analytics.totalNodes} NODES ACTIVE
            </span>
            <span className="an-bar-percent">{utilization}%</span>
          </div>
          <div className="an-bar-track">
            <div className="an-bar-fill" style={{ width: `${utilization}%` }}></div>
          </div>
        </div>
      </div>
    </div>
  );
};
